import React, {useEffect, useRef, useState, useCallback} from 'react';
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {withStyles} from "@mui/styles";
import WowzaWebRTCPlay from "../../../Preview/lib/WowzaWebRTCPlay";
import ExternalStreamSource from "./ExternalStreamSource";
import Player from "../../../Preview/Player";

console.log("[AutoStreamPreview] Module loaded");

type Properties = {
    classes: any,
    liveEvent: any;
}

const AutoStreamPreview = (props: Properties) => {
    console.log("[AutoStreamPreview] ====== COMPONENT FUNCTION CALLED ======");
    console.log("[AutoStreamPreview] Props received:", props);

    const signalingUrl = "wss://5fa0b37d2a2bd.streamlock.net/webrtc-session.json";
    const wsRef = useRef<WowzaWebRTCPlay | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const streamCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const { liveEvent } = props;
    const [isConnected, setIsConnected] = useState(false);
    const isTestingRef = useRef(false);
    const testConnectionRef = useRef<(() => void) | null>(null);
    const streamName = liveEvent?.event_transmission_accesses?.[0]?.reference;

    console.log("[AutoStreamPreview] Component render:", {
        hasLiveEvent: !!liveEvent,
        liveEventKeys: liveEvent ? Object.keys(liveEvent) : null,
        streamName,
        isConnected,
        isTesting: isTestingRef.current,
        eventTransmissionAccesses: liveEvent?.event_transmission_accesses
    });

    const handleConnectionSuccess = useCallback(() => {
        console.log("[AutoStreamPreview] ✅ handleConnectionSuccess called - Stream connected!");
        console.log("[AutoStreamPreview] Setting isConnected to true");
        setIsConnected(true);
        isTestingRef.current = false;

        // Clean up interval
        if (streamCheckIntervalRef.current) {
            clearInterval(streamCheckIntervalRef.current);
            streamCheckIntervalRef.current = null;
        }

        // Clean up test video element
        const testVideoElement = document.getElementById('test-player-video');
        if (testVideoElement) {
            testVideoElement.remove();
        }

        // Stop the test connection
        if (wsRef.current) {
            wsRef.current.stop();
            wsRef.current = null;
        }

        // Clear any pending retry
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);

    const handleConnectionFailed = useCallback(() => {
        console.log("[AutoStreamPreview] ❌ handleConnectionFailed called");
        // Use a ref to check current state without closure issues
        setIsConnected((current) => {
            console.log("[AutoStreamPreview] handleConnectionFailed - current isConnected state:", current);
            // If we were connected and now failed, switch back to ExternalStreamSource
            if (current) {
                console.log("[AutoStreamPreview] Stream disconnected, switching back to ExternalStreamSource");
                return false;
            }
            return current; // Keep current state
        });

        isTestingRef.current = false;
        console.log("[AutoStreamPreview] handleConnectionFailed - Scheduling retry in 3 seconds");

        // Clean up test connection
        if (wsRef.current) {
            wsRef.current.stop();
            wsRef.current = null;
        }

        // Retry after 3 seconds
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }

        retryTimeoutRef.current = setTimeout(() => {
            console.log("[AutoStreamPreview] Retry timeout fired, checking if should retry...");
            setIsConnected((currentState) => {
                console.log("[AutoStreamPreview] Retry check - currentState:", currentState, "isTesting:", isTestingRef.current, "hasTestConnection:", !!testConnectionRef.current);
                if (!currentState && !isTestingRef.current && testConnectionRef.current) {
                    console.log("[AutoStreamPreview] Retrying connection...");
                    isTestingRef.current = true;
                    testConnectionRef.current();
                } else {
                    console.log("[AutoStreamPreview] Skipping retry - conditions not met");
                }
                return currentState;
            });
        }, 3000);
    }, []);

    const testConnection = useCallback(() => {
        console.log("[AutoStreamPreview] 🔄 testConnection called");
        console.log("[AutoStreamPreview] testConnection - streamName:", streamName);

        // Clean up previous connection if exists
        if (wsRef.current) {
            console.log("[AutoStreamPreview] Cleaning up previous connection");
            wsRef.current.stop();
            wsRef.current = null;
        }

        // Create a temporary video element for testing
        let testVideoElement = document.getElementById('test-player-video') as HTMLVideoElement;
        if (!testVideoElement) {
            console.log("[AutoStreamPreview] Creating new test video element");
            testVideoElement = document.createElement('video');
            testVideoElement.id = 'test-player-video';
            testVideoElement.style.display = 'none';
            document.body.appendChild(testVideoElement);
        } else {
            console.log("[AutoStreamPreview] Reusing existing test video element");
        }

        console.log("[AutoStreamPreview] Test video element:", {
            id: testVideoElement.id,
            srcObject: testVideoElement.srcObject,
            src: testVideoElement.src,
            readyState: testVideoElement.readyState
        });

        // Listen for when the video element receives a stream (more reliable than connection state)
        const checkForStream = () => {
            const hasSrcObject = !!testVideoElement.srcObject;
            const hasSrc = !!testVideoElement.src;
            const readyState = testVideoElement.readyState;

            console.log("[AutoStreamPreview] checkForStream - checking video element:", {
                hasSrcObject,
                hasSrc,
                readyState,
                srcObjectType: testVideoElement.srcObject?.constructor?.name,
                videoWidth: testVideoElement.videoWidth,
                videoHeight: testVideoElement.videoHeight
            });

            if (hasSrcObject || hasSrc) {
                console.log("[AutoStreamPreview] ✅ Stream detected on video element! Calling handleConnectionSuccess");
                handleConnectionSuccess();
                return true;
            }
            return false;
        };

        // Check periodically if stream is available
        console.log("[AutoStreamPreview] Starting periodic stream check (every 500ms)");
        let checkCount = 0;
        streamCheckIntervalRef.current = setInterval(() => {
            checkCount++;
            if (checkCount % 10 === 0) { // Log every 5 seconds (10 * 500ms)
                console.log("[AutoStreamPreview] Periodic check running... (check #" + checkCount + ")");
            }
            if (checkForStream()) {
                console.log("[AutoStreamPreview] Stream found in periodic check, clearing interval");
                if (streamCheckIntervalRef.current) {
                    clearInterval(streamCheckIntervalRef.current);
                    streamCheckIntervalRef.current = null;
                }
            }
        }, 500);

        // Also listen for video events
        const onLoadedMetadata = () => {
            console.log("[AutoStreamPreview] 📹 onLoadedMetadata event fired - Video metadata loaded!");
            console.log("[AutoStreamPreview] Video element state:", {
                srcObject: testVideoElement.srcObject,
                src: testVideoElement.src,
                readyState: testVideoElement.readyState,
                videoWidth: testVideoElement.videoWidth,
                videoHeight: testVideoElement.videoHeight
            });
            if (streamCheckIntervalRef.current) {
                clearInterval(streamCheckIntervalRef.current);
                streamCheckIntervalRef.current = null;
            }
            handleConnectionSuccess();
        };

        const onPlaying = () => {
            console.log("[AutoStreamPreview] ▶️ onPlaying event fired - Video playing!");
            console.log("[AutoStreamPreview] Video element state:", {
                srcObject: testVideoElement.srcObject,
                src: testVideoElement.src,
                readyState: testVideoElement.readyState,
                paused: testVideoElement.paused,
                currentTime: testVideoElement.currentTime
            });
            if (streamCheckIntervalRef.current) {
                clearInterval(streamCheckIntervalRef.current);
                streamCheckIntervalRef.current = null;
            }
            handleConnectionSuccess();
        };

        // Also listen for other video events for debugging
        const onLoadedData = () => {
            console.log("[AutoStreamPreview] 📊 onLoadedData event fired");
        };

        const onCanPlay = () => {
            console.log("[AutoStreamPreview] 🎬 onCanPlay event fired");
        };

        console.log("[AutoStreamPreview] Adding event listeners to video element");
        testVideoElement.addEventListener('loadedmetadata', onLoadedMetadata);
        testVideoElement.addEventListener('playing', onPlaying);
        testVideoElement.addEventListener('loadeddata', onLoadedData);
        testVideoElement.addEventListener('canplay', onCanPlay);

        console.log("[AutoStreamPreview] Creating new WowzaWebRTCPlay instance");
        wsRef.current = new WowzaWebRTCPlay();
        wsRef.current.on({
            onError: (error: any) => {
                console.log("[AutoStreamPreview] ❌ onError callback fired:", error);
                console.log("[AutoStreamPreview] Error details:", JSON.stringify(error, null, 2));
                if (streamCheckIntervalRef.current) {
                    clearInterval(streamCheckIntervalRef.current);
                    streamCheckIntervalRef.current = null;
                }
                testVideoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
                testVideoElement.removeEventListener('playing', onPlaying);
                testVideoElement.removeEventListener('loadeddata', onLoadedData);
                testVideoElement.removeEventListener('canplay', onCanPlay);
                handleConnectionFailed();
            },
            onStateChanged: (state: any) => {
                console.log("[AutoStreamPreview] 🔄 onStateChanged callback fired");
                console.log("[AutoStreamPreview] Connection state:", state.connectionState);

                // Log full state only for important state changes
                if (state.connectionState !== 'stopped') {
                    console.log("[AutoStreamPreview] Full state object:", JSON.stringify(state, null, 2));
                }

                // Check if stream is available when connection state changes
                if (state.connectionState === 'connected') {
                    console.log("[AutoStreamPreview] ✅ Connection state is 'connected'!");
                    console.log("[AutoStreamPreview] Checking for stream in 1 second...");
                    // Give it a moment for the stream to be assigned
                    setTimeout(() => {
                        console.log("[AutoStreamPreview] Checking for stream after connection...");
                        if (checkForStream()) {
                            console.log("[AutoStreamPreview] Stream found after connection, cleaning up...");
                            if (streamCheckIntervalRef.current) {
                                clearInterval(streamCheckIntervalRef.current);
                                streamCheckIntervalRef.current = null;
                            }
                            testVideoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
                            testVideoElement.removeEventListener('playing', onPlaying);
                            testVideoElement.removeEventListener('loadeddata', onLoadedData);
                            testVideoElement.removeEventListener('canplay', onCanPlay);
                        } else {
                            console.log("[AutoStreamPreview] Stream not found yet after connection, will keep checking...");
                        }
                    }, 1000);
                } else if (state.connectionState === 'connecting') {
                    console.log("[AutoStreamPreview] 🔄 Connection state is 'connecting' - progress!");
                } else if (state.connectionState === 'failed' || state.connectionState === 'disconnected') {
                    console.log("[AutoStreamPreview] ❌ Connection failed or disconnected, cleaning up...");
                    if (streamCheckIntervalRef.current) {
                        clearInterval(streamCheckIntervalRef.current);
                        streamCheckIntervalRef.current = null;
                    }
                    testVideoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
                    testVideoElement.removeEventListener('playing', onPlaying);
                    testVideoElement.removeEventListener('loadeddata', onLoadedData);
                    testVideoElement.removeEventListener('canplay', onCanPlay);
                    handleConnectionFailed();
                } else if (state.connectionState === 'stopped') {
                    // Don't log every stopped state, it's the initial state
                } else {
                    console.log("[AutoStreamPreview] Connection state:", state.connectionState, "- continuing to wait...");
                }
            }
        });

        console.log("[AutoStreamPreview] Setting video element on WowzaWebRTCPlay");
        wsRef.current.set({
            videoElementPlay: testVideoElement,
        });

        const settings = {
            applicationName: "restream",
            sdpURL: signalingUrl,
            streamName: streamName
        };

        console.log("[AutoStreamPreview] Setting WowzaWebRTCPlay settings:", settings);
        wsRef.current.set(settings).then(() => {
            console.log("[AutoStreamPreview] Settings applied, calling play()...");
            try {
                wsRef.current?.play();
                console.log("[AutoStreamPreview] play() called successfully");
                console.log("[AutoStreamPreview] Waiting for connection state changes...");

                // Also check the video element state after a delay
                setTimeout(() => {
                    console.log("[AutoStreamPreview] Video element state after 2 seconds:", {
                        srcObject: testVideoElement.srcObject,
                        src: testVideoElement.src,
                        readyState: testVideoElement.readyState,
                        videoWidth: testVideoElement.videoWidth,
                        videoHeight: testVideoElement.videoHeight,
                        paused: testVideoElement.paused
                    });
                }, 2000);
            } catch (playError) {
                console.log("[AutoStreamPreview] ❌ Error calling play():", playError);
            }
        }).catch((error: any) => {
            console.log("[AutoStreamPreview] ❌ Error in set() promise:", error);
            console.log("[AutoStreamPreview] Error details:", JSON.stringify(error, null, 2));
            if (streamCheckIntervalRef.current) {
                clearInterval(streamCheckIntervalRef.current);
                streamCheckIntervalRef.current = null;
            }
            testVideoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
            testVideoElement.removeEventListener('playing', onPlaying);
            testVideoElement.removeEventListener('loadeddata', onLoadedData);
            testVideoElement.removeEventListener('canplay', onCanPlay);
            handleConnectionFailed();
        });
    }, [streamName, handleConnectionSuccess, handleConnectionFailed]);

    // Store testConnection in ref so it can be called from handleConnectionFailed
    testConnectionRef.current = testConnection;

    useEffect(() => {
        console.log("[AutoStreamPreview] useEffect triggered:", {
            hasLiveEvent: !!liveEvent,
            streamName,
            isConnected,
            isTesting: isTestingRef.current
        });

        if (liveEvent && streamName && !isConnected && !isTestingRef.current) {
            console.log("[AutoStreamPreview] ✅ Conditions met, starting test connection");
            isTestingRef.current = true;
            testConnection();
        } else {
            console.log("[AutoStreamPreview] ⏸️ Conditions not met, not starting test:", {
                hasLiveEvent: !!liveEvent,
                hasStreamName: !!streamName,
                isConnected,
                isTesting: isTestingRef.current
            });
        }

        return () => {
            // Cleanup on unmount
            if (wsRef.current) {
                wsRef.current.stop();
                wsRef.current = null;
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            if (streamCheckIntervalRef.current) {
                clearInterval(streamCheckIntervalRef.current);
            }
            // Clean up test video element
            const testVideoElement = document.getElementById('test-player-video');
            if (testVideoElement) {
                testVideoElement.remove();
            }
        };
    }, [liveEvent, streamName, isConnected, testConnection]);

    // Show Player when connected, ExternalStreamSource when not connected
    console.log("[AutoStreamPreview] Render decision - isConnected:", isConnected);

    // Monitor Player for disconnections - improved detection
    useEffect(() => {
        if (!isConnected) return;

        let lastStreamState = false;
        let lastPlayingState = false;

        // Monitor the Player's connection state more reliably
        const checkPlayerConnection = setInterval(() => {
            const playerVideo = document.getElementById('player-video') as HTMLVideoElement;
            if (playerVideo) {
                const hasStream = !!(playerVideo.srcObject || playerVideo.src);
                const isPlaying = playerVideo.currentTime > 0 && !playerVideo.paused && !playerVideo.ended;
                const readyState = playerVideo.readyState;

                // Track state changes
                const streamLost = lastStreamState && !hasStream;
                const stoppedPlaying = lastPlayingState && !isPlaying && readyState >= 2; // readyState 2 = HAVE_CURRENT_DATA

                // If stream was there but now gone, or was playing but stopped (and not user-paused)
                if (streamLost || (stoppedPlaying && readyState === 0)) {
                    console.log("[AutoStreamPreview] Player lost stream or stopped playing, switching back to ExternalStreamSource");
                    console.log("[AutoStreamPreview] Stream state:", { hasStream, isPlaying, readyState, streamLost, stoppedPlaying });
                    setIsConnected(false);
                }

                lastStreamState = hasStream;
                lastPlayingState = isPlaying;
            }
        }, 1000); // Check every second for faster detection

        return () => clearInterval(checkPlayerConnection);
    }, [isConnected]);

    if (isConnected) {
        console.log("[AutoStreamPreview] Rendering Player component");
        return <Player liveEvent={liveEvent} />;
    }

    console.log("[AutoStreamPreview] Rendering ExternalStreamSource component");
    return <ExternalStreamSource liveEvent={liveEvent} />;
};

const mapStateToProps = function (state: any) {
    return {}
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {};
}

export default withStyles({})(connect(mapStateToProps, mapDispatchToProps)(AutoStreamPreview));

